// TICKET-9 🟡 Fichier sans aucun test (177 lignes, migrations Dexie)
// À tester indirectement via workspaceStorage.test.ts et backup.test.ts
// Voir aussi : TICKET-4 (devis.ts), TICKET-12 (consolidation legacy)

import Dexie, { Table } from 'dexie';
import { DevisRecord } from '@shared/domain/devis';
import { ClientRiskRecord, CraMonth, JournalEntry, JournalLink, MissionLead, MissionMemory } from '@shared/types';
import { MissioInvoiceRecord } from '@shared/domain/missioBridge';
import { ProposalRecord } from '@shared/domain/proposal';

type BaseStoredEntity = {
  id: string;
  updatedAt?: number | string;
};

export type ProspectRecord = BaseStoredEntity & {
  name?: string;
  company?: string;
  status?: string;
  source?: string;
};

export type MissionRecord = BaseStoredEntity & {
  prospectId?: string;
  title?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
};

export type TaskRecord = BaseStoredEntity & {
  linkedMissionId?: string;
  title?: string;
  status?: string;
  priority?: string;
  dueDate?: string;
};

export type FormRecord = BaseStoredEntity & {
  title?: string;
};

export type FormResponseRecord = BaseStoredEntity & {
  formId?: string;
  submittedAt?: string;
};

export class HubDatabase extends Dexie {
  prospects!: Table<ProspectRecord, string>;
  missions!: Table<MissionRecord, string>;
  tasks!: Table<TaskRecord, string>;
  forms!: Table<FormRecord, string>;
  formResponses!: Table<FormResponseRecord, string>;
  devis!: Table<DevisRecord, string>;
  cras!: Table<CraMonth, string>;
  missioInvoices!: Table<MissioInvoiceRecord, string>;
  missionLeads!: Table<MissionLead, string>;
  missionMemories!: Table<MissionMemory, string>;
  proposals!: Table<ProposalRecord, string>;
  clientRisks!: Table<ClientRiskRecord, string>;
  journalEntries!: Table<JournalEntry, string>;
  journalLinks!: Table<JournalLink, string>;

  constructor() {
    super('indxone-hub-local');

    this.version(1).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
    });

    this.version(2).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
    });

    this.version(3).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
    });

    this.version(4).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, updatedAt',
      proposals: 'id, updatedAt',
    });

    this.version(5).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, updatedAt',
      proposals: 'id, updatedAt',
    });

    this.version(6).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, updatedAt',
      missionMemories: 'id, clientName, rating, createdAt, updatedAt',
      proposals: 'id, updatedAt',
    });

    this.version(7).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, order, updatedAt',
      missionMemories: 'id, clientName, rating, createdAt, updatedAt',
      proposals: 'id, updatedAt',
    });

    this.version(8).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, order, updatedAt',
      missionMemories: 'id, clientName, rating, createdAt, updatedAt',
      proposals: 'id, updatedAt',
      clientRisks: 'id, level, evaluatedAt, updatedAt',
    });

    this.version(9).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, projectId, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, order, updatedAt',
      missionMemories: 'id, clientName, rating, createdAt, updatedAt',
      proposals: 'id, updatedAt',
      clientRisks: 'id, level, evaluatedAt, updatedAt',
    });

    this.version(10).stores({
      prospects: 'id, company, status, source, updatedAt',
      missions: 'id, prospectId, status, startDate, endDate, updatedAt',
      tasks: 'id, linkedMissionId, status, priority, dueDate, updatedAt',
      forms: 'id, updatedAt',
      formResponses: 'id, formId, submittedAt, updatedAt',
      devis: 'id, projectId, missionId, status, updatedAt, num, clientName',
      cras: 'id, projectId, year, month, updatedAt',
      missioInvoices: 'id, missionId, status, date, updatedAt',
      missionLeads: 'id, status, order, updatedAt',
      missionMemories: 'id, clientName, rating, createdAt, updatedAt',
      proposals: 'id, updatedAt',
      clientRisks: 'id, level, evaluatedAt, updatedAt',
      journalEntries: 'id, projectId, missionId, type, status, dueAt, updatedAt, legacyNoteId',
      journalLinks: 'id, journalEntryId, targetType, targetId, relation, createdAt',
    });
  }
}

export const hubDb = new HubDatabase();
